import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  type ForwardRefExoticComponent,
} from "react";
import cx from "classnames";
import { Link } from "@/features/shell/components/shared/Link/Link";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { Router, useLocation } from "@gatsbyjs/reach-router";
import { useLocalePrefix } from "@/hooks/useLocale";
import { motion } from "motion/react";
import { ScrollArea } from "@picklebet/ui";
import escapeRegExp from "lodash/escapeRegExp";
import { v4 as uuidv4 } from "uuid";
import { Badge } from "@picklebet/ui";

import * as styles from "./Tabs.module.scss";

type Tab = {
  to?: string;
  value?: string;
  badge?: string | React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>, "value">;

type TabsProps = {
  basePath?: string;
  isValueDerivedFromRouter?: boolean; // determines if tab value is provided or derived from router
} & React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>;

type TabsContentProps = {
  className?: string;
  children: React.ReactNode;
};

type TabListProps = {
  widget?: React.ReactNode;
  containerClassName?: string;
  dragToScroll?: boolean;
} & React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>;

export const PathContext = createContext<string>("/");
export const SelectedTabContext = createContext<string | null>(null);
export const UniqueIdContext = createContext<string | null>(null);

const parseValueFromPathname = (
  prefix: string,
  basePath: string,
  pathname: string,
) =>
  pathname.match(
    new RegExp(`^${escapeRegExp(prefix + basePath)}(/[\\w-]*)`),
  )?.[1] || "";

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  TabsProps
>(
  (
    {
      basePath = "",
      isValueDerivedFromRouter = false,
      value,
      onValueChange,
      ...props
    },
    ref,
  ) => {
    const prefix = useLocalePrefix();
    const { pathname } = useLocation();
    const [uniqueId] = useState(uuidv4());

    const parsedValue = isValueDerivedFromRouter
      ? parseValueFromPathname(prefix, basePath, pathname)
      : value;

    useEffect(() => {
      if (parsedValue && onValueChange && isValueDerivedFromRouter) {
        onValueChange(parsedValue);
      }
    }, [parsedValue]);

    return (
      <UniqueIdContext.Provider value={uniqueId}>
        <PathContext.Provider value={basePath || "/"}>
          <SelectedTabContext.Provider
            value={parsedValue || props.defaultValue || null}
          >
            <TabsPrimitive.Root
              onValueChange={onValueChange}
              value={parsedValue || undefined}
              ref={ref}
              {...props}
            />
          </SelectedTabContext.Provider>
        </PathContext.Provider>
      </UniqueIdContext.Provider>
    );
  },
);

Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabListProps
>(({ className, containerClassName, widget, dragToScroll, ...props }, ref) => {
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cx(styles.listRoot, containerClassName)}
      {...props}
    >
      <ScrollArea.Root
        orientation="horizontal"
        className={styles.scrollAreaRoot}
      >
        <ScrollArea.Viewport
          className={styles.tabContainer}
          dragToScroll={dragToScroll}
        >
          <div className={cx(styles.list, className)}>{props.children}</div>
        </ScrollArea.Viewport>
        <ScrollArea.Shadows />
      </ScrollArea.Root>
      {widget && <div className={styles.widget}>{widget}</div>}
    </TabsPrimitive.List>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const Tab = React.forwardRef<React.Ref<typeof TabsPrimitive.Trigger>, Tab>(
  (
    { className, to = "", value = "", badge, children, onClick, ...props },
    ref,
  ) => {
    const basePath = useContext(PathContext);
    const selectedTab = useContext(SelectedTabContext);
    const innerRef = useRef<HTMLButtonElement>(null);
    const toWithoutSearch = to.replace(/\?.*$/, "");
    const isActive =
      toWithoutSearch && selectedTab !== "/"
        ? toWithoutSearch.endsWith(selectedTab || "") ||
          toWithoutSearch.startsWith(selectedTab || "")
        : selectedTab === value;

    useImperativeHandle(ref, () => ({
      get current() {
        // need to revisit this, tabs need to migrate away from the legacy
        // React.Children api perhaps that's a better time to reshuffle the
        // structure and remove the imperative handle
        return innerRef.current as unknown as ForwardRefExoticComponent<
          TabsPrimitive.TabsTriggerProps &
            React.RefAttributes<HTMLButtonElement>
        >;
      },
    }));

    useEffect(() => {
      if (!isActive) return;

      const tab = innerRef.current;
      if (!tab) return;

      const scrollParent = tab.closest<HTMLElement>(
        "[data-radix-scroll-area-viewport]",
      );
      if (!scrollParent) return;

      const tabRect = tab.getBoundingClientRect();
      const parentRect = scrollParent.getBoundingClientRect();
      const left = scrollParent.scrollLeft + (tabRect.left - parentRect.left);

      scrollParent.scrollTo({
        left,
        behavior: "smooth",
      });
    }, [isActive]);

    if (props.hidden) {
      return null;
    }

    if (toWithoutSearch) {
      return (
        <TabsPrimitive.Trigger
          ref={innerRef}
          className={cx(styles.trigger, className)}
          value={parseValueFromPathname("", "", toWithoutSearch)}
          asChild
          onClick={onClick}
          {...props}
        >
          {/* reach router types are weird, had to override styles */}
          <Link to={`${basePath}${to}`}>
            <>
              {children}
              {typeof badge === "string" ? <Badge>{badge}</Badge> : badge}
            </>
            <Underline isActive={isActive} />
          </Link>
        </TabsPrimitive.Trigger>
      );
    }

    return (
      <TabsPrimitive.Trigger
        ref={innerRef}
        className={cx(styles.trigger, className)}
        value={value}
        {...props}
      >
        {children}
        {typeof badge === "string" ? <Badge>{badge}</Badge> : badge}
        <Underline isActive={isActive} />
      </TabsPrimitive.Trigger>
    );
  },
);
Tab.displayName = TabsPrimitive.Trigger.displayName;

const Underline = ({ isActive }: { isActive: boolean }) => {
  const uniqueId = useContext(UniqueIdContext);
  if (!isActive) return null;

  return (
    <motion.div layout layoutRoot className={styles.underlineContainer}>
      <motion.div
        className={styles.underline}
        layoutId={`underline-${uniqueId}`}
        transition={{ duration: 0.15 }}
      />
    </motion.div>
  );
};

const TabsContent = ({ children, className }: TabsContentProps) => {
  const basePath = useContext(PathContext);
  const prefix = useLocalePrefix();

  // we need to use reach router here because radix tabs doesn't support nested routes
  return (
    <Router
      className={className}
      primary={false}
      basepath={`${prefix}${basePath}`}
    >
      {React.Children.map(
        children as React.ReactElement<{ path: string }>[],
        (child) => {
          if (!React.isValidElement(child)) return null;
          return React.cloneElement(
            child,
            child.props,
            <TabContent value={child.props.path} />,
          );
        },
      )}
    </Router>
  );
};

const TabContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => {
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cx(styles.content, className)}
      {...props}
    />
  );
});
TabContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, Tab, TabContent, TabsContent };
